import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formatNumberWithCommas, parseNumberWithCommas } from '@/lib/utils';
import type { AssetMetadata } from '@/types/asset';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const financialSchema = z.object({
  purchase_date: z.string().optional().nullable(),
  purchase_price: z.number().optional().nullable(),
  closing_costs: z.number().optional().nullable(),
  has_mortgage: z.boolean(),
  loan_amount: z.number().optional().nullable(),
  loan_due_date: z.string().optional().nullable(),
  down_payment: z.number().optional().nullable(),
  is_rental: z.boolean(),
  market_rent: z.number().optional().nullable(),
});

export type FinancialFormValues = z.infer<typeof financialSchema>;

interface FinancialDetailsStepProps {
  onBack: () => void;
  onNext: (data: FinancialFormValues) => void;
  isLoading: boolean;
  parcelData?: AssetMetadata | null;
}

// Helper function to format date for HTML date input
const formatDateForInput = (dateValue: string | null | undefined): string => {
  if (!dateValue) return '';

  try {
    // If it's already a date string, parse it
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';

    // Return in YYYY-MM-DD format for HTML date input
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

export default function FinancialDetailsStep({
  onBack,
  onNext,
  isLoading,
  parcelData,
}: FinancialDetailsStepProps) {
  const form = useForm<FinancialFormValues>({
    resolver: zodResolver(financialSchema),
    defaultValues: {
      purchase_date: parcelData?.purchase_date || null,
      purchase_price: parcelData?.purchase_price || null,
      has_mortgage:
        parcelData?.loan_amount !== null && parcelData?.loan_amount > 0,
      loan_amount: parcelData?.loan_amount || null,
      loan_due_date: parcelData?.loan_due_date || null,
      down_payment: parcelData?.down_payment || null,
      is_rental:
        parcelData?.market_rent !== null && parcelData?.market_rent > 0,
      market_rent: parcelData?.market_rent || null,
    },
  });

  const handleSubmit = (values: FinancialFormValues) => {
    onNext(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Purchase Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="purchase_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Purchase Date</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={formatDateForInput(field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="purchase_price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Purchase Price</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="0.00"
                        className="pl-8"
                        {...field}
                        value={formatNumberWithCommas(field.value)}
                        onChange={(e) => {
                          const value = parseNumberWithCommas(e.target.value);
                          field.onChange(value);
                        }}
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="closing_costs"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Closing Costs</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="0.00"
                        className="pl-8"
                        {...field}
                        value={formatNumberWithCommas(field.value)}
                        onChange={(e) => {
                          const value = parseNumberWithCommas(e.target.value);
                          field.onChange(value);
                        }}
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Mortgage Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="has_mortgage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Does this property have a mortgage?</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) =>
                        field.onChange(value === 'true')
                      }
                      defaultValue={field.value ? 'true' : 'false'}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="has-mortgage-yes" />
                        <label htmlFor="has-mortgage-yes">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="has-mortgage-no" />
                        <label htmlFor="has-mortgage-no">No</label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />

            {form.watch('has_mortgage') && (
              <>
                <FormField
                  control={form.control}
                  name="loan_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loan Amount</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="text"
                            placeholder="0.00"
                            className="pl-8"
                            {...field}
                            value={formatNumberWithCommas(field.value)}
                            onChange={(e) => {
                              const value = parseNumberWithCommas(
                                e.target.value,
                              );
                              field.onChange(value);
                            }}
                          />
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                            $
                          </span>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="down_payment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Down Payment</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="text"
                            placeholder="0.00"
                            className="pl-8"
                            {...field}
                            value={formatNumberWithCommas(field.value)}
                            onChange={(e) => {
                              const value = parseNumberWithCommas(
                                e.target.value,
                              );
                              field.onChange(value);
                            }}
                          />
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                            $
                          </span>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="loan_due_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loan Due Date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={formatDateForInput(field.value)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rental Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="is_rental"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Is this a rental property?</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) =>
                        field.onChange(value === 'true')
                      }
                      defaultValue={field.value ? 'true' : 'false'}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="is-rental-yes" />
                        <label htmlFor="is-rental-yes">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="is-rental-no" />
                        <label htmlFor="is-rental-no">No</label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />

            {form.watch('is_rental') && (
              <FormField
                control={form.control}
                name="market_rent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Market Rent</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type="text"
                          placeholder="0.00"
                          className="pl-8"
                          {...field}
                          value={formatNumberWithCommas(field.value)}
                          onChange={(e) => {
                            const value = parseNumberWithCommas(e.target.value);
                            field.onChange(value);
                          }}
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
